import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChefHat, Carrot, FileText, Sparkles, ArrowRight, Check, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/Logo";
import { StatTiles, ObservationCard, MarginNote, catLine } from "@/components/menu/MenuInsights";
import ImportDishesDialog from "@/components/menu/ImportDishesDialog";
import api from "@/lib/api";
import { eur, eur0 } from "@/lib/format";
import { t } from "@/i18n";

function Panel({ title, children, testId }) {
  return (
    <section className="min-w-0 rounded-xl border bg-card p-4 sm:p-5" data-testid={testId}>
      <h2 className="mb-4 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function NextSteps({ imported }) {
  const steps = [
    [ChefHat, t("menuAnalysisDetail.ajoutez_vos_plats_aux_recettes"), t("menuAnalysisDetail.chaque_plat_garde_son_prix_de_vente"), imported],
    [Carrot, t("menuAnalysisDetail.completez_les_ingredients_en_1_clic"), t("menuAnalysisDetail.l_ia_propose_ingredients_et_quantites_pour"), false, imported ? "/recipes?fill=1" : null],
    [FileText, t("menuAnalysisDetail.importez_une_facture_fournisseur"), t("menuAnalysisDetail.vos_vrais_prix_d_achat_alimentent_le_cout"), false, "/invoices"],
    [Sparkles, t("menuAnalysisDetail.obtenez_marges_et_prix_conseilles"), t("menuAnalysisDetail.food_cost_marge_par_plat_et_recommandation"), false, "/dashboard"],
  ];
  return (
    <ol className="space-y-2">
      {steps.map(([Icon, t, s, done, to], i) => {
        const body = (
          <div className="flex items-center gap-3 rounded-xl border bg-background p-3">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${done ? "bg-emerald-500 text-white" : "bg-muted"}`}>{done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}</span>
            <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{t}</span><span className="block text-xs text-muted-foreground">{s}</span></span>
            {to && <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          </div>
        );
        return <li key={t} data-testid={`menu-next-step-${i}`}>{to ? <Link to={to}>{body}</Link> : body}</li>;
      })}
    </ol>
  );
}

export default function MenuAnalysisDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get(`/menu-analysis/${id}`).then(({ data }) => setA(data)).catch(() => setError(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="py-16 text-center text-sm text-muted-foreground" data-testid="menu-detail-error">{t("menuAnalysisDetail.analyse_introuvable")} <Link to="/ma-carte" className="underline">{t("menuAnalysisDetail.retour")}</Link></p>;
  if (!a) return <div className="flex h-64 items-center justify-center" data-testid="menu-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const imported = a.imported_count > 0;
  return (
    <>
      <PageHeader eyebrow={t("menuAnalysisDetail.analyse_complete")} title={a.restaurant_name || t("common.votre_carte")} description={catLine(a.stats.categories)}
        actions={<Button onClick={() => setImportOpen(true)} variant={imported ? "outline" : "default"} data-testid="menu-import-btn"><Plus className="mr-2 h-4 w-4 shrink-0" /><span className="truncate">{imported ? t("menuAnalysisDetail.ajouter_d_autres_plats") : t("menuAnalysisDetail.ajouter_a_mes_recettes")}</span></Button>} />
      <div className="space-y-6">
        {a.summary && <p className="max-w-3xl text-sm leading-relaxed sm:text-base" data-testid="menu-detail-summary">{a.summary}</p>}
        <StatTiles stats={a.stats} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2">
            <h2 className="text-base font-bold md:text-lg">{t("menu.n_observations", { v0: a.observations.length })}</h2>
            {a.observations.map((o, i) => <ObservationCard key={i} o={o} i={i} />)}
            <MarginNote />
          </div>
          <div className="space-y-4">
            <Panel title={t("menuAnalysisDetail.prochaines_etapes")} testId="menu-next-steps"><NextSteps imported={imported} /></Panel>
            {a.stats.price_buckets.length > 0 && (
              <Panel title={t("menuAnalysisDetail.repartition_des_prix")} testId="menu-price-distribution">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={a.stats.price_buckets} margin={{ left: -24, right: 4 }}>
                    <XAxis dataKey="label" tickFormatter={(v, i) => { const b = a.stats.price_buckets[i]; return b?.hi ? `${eur0(b.lo)}–${eur0(b.hi)}` : v; }} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} interval={0} angle={-30} textAnchor="end" height={48} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} plat(s)`, ""]} />
                    <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            )}
          </div>
        </div>
        <Panel title={t("menuAnalysisDetail.detail_par_rubrique")} testId="menu-categories-detail">
          <div className="space-y-5">
            {a.menu.categories.map((c, ci) => {
              const st = a.stats.categories[ci];
              return (
                <div key={ci} data-testid={`menu-category-${ci}`}>
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 border-b pb-2">
                    <h3 className="font-semibold">{c.name}</h3>
                    <span className="num text-xs text-muted-foreground">{t("menu.n_dishes", { v0: st.dish_count })}{st.avg ? t("menuAnalysisDetail.moy_x_x_x", { v0: eur(st.avg), v1: eur(st.min), v2: eur(st.max) }) : ""}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {c.dishes.map((d, di) => (
                      <li key={di} className="flex items-start justify-between gap-3 text-sm">
                        <span className="min-w-0"><span className="font-medium">{d.name}</span>{d.description && <span className="block text-xs text-muted-foreground">{d.description}</span>}</span>
                        <span className="num shrink-0">{d.price ? eur(d.price) : "—"}{d.price_note && <span className="block text-right text-[11px] text-muted-foreground">{d.price_note}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {a.menu.formulas.length > 0 && (
              <div>
                <h3 className="mb-2 border-b pb-2 font-semibold">{t("menuAnalysisDetail.formules")}</h3>
                {a.menu.formulas.map((f, i) => <p key={i} className="flex justify-between gap-3 text-sm"><span>{f.name}{f.content && <span className="block text-xs text-muted-foreground">{f.content}</span>}</span><span className="num shrink-0">{f.price ? eur(f.price) : "—"}</span></p>)}
              </div>
            )}
          </div>
        </Panel>
      </div>
      {importOpen && <ImportDishesDialog analysis={a} open={importOpen} onOpenChange={setImportOpen} onDone={() => { setImportOpen(false); load(); }} />}
    </>
  );
}
